import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from '../login/loginpagestyles.module.css';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';

function ResetPassword() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userName, setUserName] = useState('');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: '',
    strength: ''
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/validate-reset-token/${token}`);
      const data = await response.json();

      if (response.ok && data.valid) {
        setTokenValid(true);
        setUserName(data.userName || t('resetPassword.defaultUserName'));
      } else {
        setError(data.error || t('resetPassword.invalidLink'));
      }
    } catch (err) {
      setError(t('resetPassword.connectionErrorLater'));
    } finally {
      setLoading(false);
    }
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    const feedback = [];

    if (password.length >= 8) score++;
    else feedback.push(t('resetPassword.strengthMin8'));

    if (password.length >= 12) score++;
    
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    else feedback.push(t('resetPassword.strengthCase'));

    if (/\d/.test(password)) score++;
    else feedback.push(t('resetPassword.strengthDigits'));

    if (/[^a-zA-Z0-9]/.test(password)) score++;
    else feedback.push(t('resetPassword.strengthSpecial'));

    const strength = score <= 2 ? t('resetPassword.strengthWeak') : score === 3 ? t('resetPassword.strengthMedium') : score === 4 ? t('resetPassword.strengthGood') : t('resetPassword.strengthVeryGood');

    return {
      score,
      feedback: feedback.length > 0 ? t('resetPassword.strengthAdd', { list: feedback.join(', ') }) : t('resetPassword.strengthStrong'),
      strength
    };
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setFormData({ ...formData, password });
    setPasswordStrength(checkPasswordStrength(password));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password.length < 8) {
      setError(t('resetPassword.tooShort'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('resetPassword.mismatch'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        // Odczytaj token CSRF zanim wyczyścimy localStorage (logout wymaga verifyCsrf)
        const csrf = localStorage.getItem('csrfToken') || '';
        // Wyczyść sesję, żeby przekierowanie na /login nie cofnęło na /home
        localStorage.removeItem('userId');
        localStorage.removeItem('isAdmin');
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('csrfToken');
        // Wyloguj po stronie serwera (opcjonalnie)
        try { await fetch(`${API_BASE}/logout`, { method: 'POST', credentials: 'include', headers: { 'X-CSRF-Token': csrf } }); } catch (_) {}
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      } else {
        setError(data.error || t('resetPassword.resetError'));
      }
    } catch (err) {
      setError(t('resetPassword.connectionErrorRetry'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !tokenValid) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.formContainer}>
          <h2>{t('resetPassword.checkingLink')}</h2>
          <p>{t('resetPassword.pleaseWait')}</p>
        </div>
      </div>
    );
  }

  if (error && !tokenValid) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.formContainer}>
          {/* V.9: bylo `❌`. Ostatnia emotka w renderowanym tekscie panelu; komunikat i tak
              mowi wprost, co jest nie tak, wiec znak przed nim niczego nie dokladal. */}
          <h2>{t('resetPassword.invalidLinkTitle')}</h2>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={() => navigate('/login')} className={styles.submitButton} style={{ marginTop: '20px' }}>
            {t('resetPassword.backToLogin')}
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.formContainer}>
          <h2>{t('resetPassword.successTitle')}</h2>
          <p className={styles.successMessage}>{t('resetPassword.successBody')}</p>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '10px' }}>
            {t('resetPassword.redirecting')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.loginPage}>
      <div className={styles.formContainer}>
        <h2>{t('resetPassword.title')}</h2>
        <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '20px' }}>
          {t('resetPassword.greetingBefore')} <strong>{userName}</strong>{t('resetPassword.greetingAfter')}
        </p>

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={formData.password}
            onChange={handlePasswordChange}
            placeholder={t('resetPassword.newPasswordPlaceholder')}
            className={styles.inputField}
            required
            minLength={8}
          />
          
          {formData.password && (
            <div style={{ marginBottom: '15px', textAlign: 'left' }}>
              <div style={{
                height: '4px',
                background: 'var(--input-border)',
                borderRadius: '2px',
                overflow: 'hidden',
                marginBottom: '5px'
              }}>
                <div style={{
                  height: '100%',
                  width: `${(passwordStrength.score / 5) * 100}%`,
                  background: passwordStrength.score <= 2 ? 'var(--danger)' : passwordStrength.score === 3 ? 'var(--warning)' : 'var(--success)',
                  transition: 'all 0.3s'
                }} />
              </div>
              <small style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                {passwordStrength.strength}: {passwordStrength.feedback}
              </small>
            </div>
          )}

          <input
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            placeholder={t('resetPassword.confirmPlaceholder')}
            className={styles.inputField}
            required
            minLength={8}
          />
          
          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <p style={{ fontSize: '12px', color: 'var(--danger)', marginTop: '-10px', marginBottom: '10px' }}>
              {t('resetPassword.mismatchInline')}
            </p>
          )}

          {error && <p className={styles.errorMessage}>{error}</p>}

          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={loading || formData.password !== formData.confirmPassword}
          >
            {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
